import { Button } from '#app/components/ui/button.tsx'
import { type IconName, Icon } from '#app/components/ui/icon'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { type MediaItem } from '#app/hooks/use-supabase-upload.ts'
import { cn } from '#app/utils/misc'

interface MediaCardProps {
	media: MediaItem
	isSelected?: boolean
	onClick?: () => void
	onEdit?: () => void
	className?: string
	showSelectionIndicator?: boolean
	showEditButton?: boolean
}

export function MediaCard({
	media,
	isSelected,
	onClick,
	onEdit,
	className,
	showSelectionIndicator = false,
	showEditButton = true,
}: MediaCardProps) {
	return (
		<div
			className={cn(
				'bg-background group relative flex flex-col rounded-md border transition-all duration-200',
				isSelected && 'ring-primary shadow-lg ring-2',
				onClick &&
					'hover:border-primary/50 focus-within:ring-primary cursor-pointer focus-within:ring-2 focus-within:ring-offset-2',
				className,
			)}
		>
			{/* Main clickable area for selection */}
			{onClick && (
				<button
					type="button"
					className="focus:ring-primary absolute inset-0 z-10 focus:ring-2 focus:ring-offset-2 focus:outline-none"
					onClick={onClick}
					aria-pressed={isSelected}
					aria-label={`${isSelected ? 'Deselect' : 'Select'} ${media.title || media.name || 'media item'}`}
				/>
			)}

			<div className="bg-accent flex aspect-square items-center justify-center overflow-hidden rounded-t-[inherit]">
				{media.mimeType?.startsWith('image/') ? (
					<img
						src={media.url || ''}
						alt={media.altText || media.name || ''}
						className="size-full rounded-t-[inherit] object-cover"
					/>
				) : (
					<FileIcon mimeType={media.mimeType} />
				)}

				{/* Selection Indicator */}
				{showSelectionIndicator && isSelected && (
					<div className="absolute top-2 left-2 z-20">
						<div className="text-primary-foreground flex size-6 items-center justify-center rounded-full bg-green-500">
							<Icon name="check" className="size-4" />
						</div>
					</div>
				)}

				{/* Edit Button */}
				{showEditButton && onEdit && (
					<div className="absolute top-2 right-2 z-20 opacity-0 transition-opacity group-hover:opacity-100">
						<TooltipProvider delayDuration={0}>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										type="button"
										variant="secondary"
										size="sm"
										className="size-8 p-0"
										onClick={(e) => {
											e.stopPropagation()
											onEdit()
										}}
									>
										<Icon name="pencil-2" className="size-3" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>Edit Details</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>
				)}
			</div>

			<div className="flex min-w-0 flex-col gap-0.5 border-t p-3">
				<p className="truncate text-[13px] font-medium text-wrap">
					{media.title || media.name}
				</p>
			</div>
		</div>
	)
}

// Reusable FileIcon component
export function FileIcon({ mimeType }: { mimeType?: string | null }) {
	const iconMap = {
		pdf: {
			icon: 'file-text',
			conditions: (type: string) =>
				type.includes('pdf') || type.includes('word'),
		},
		archive: {
			icon: 'archive',
			conditions: (type: string) =>
				type.includes('zip') || type.includes('archive'),
		},
		excel: {
			icon: 'file-spreadsheet',
			conditions: (type: string) => type.includes('excel'),
		},
		video: {
			icon: 'video',
			conditions: (type: string) => type.includes('video/'),
		},
		audio: {
			icon: 'headphones',
			conditions: (type: string) => type.includes('audio/'),
		},
		image: {
			icon: 'image',
			conditions: (type: string) => type.startsWith('image/'),
		},
	}

	for (const { icon, conditions } of Object.values(iconMap)) {
		if (conditions(mimeType || '')) {
			return <Icon name={icon as IconName} className="size-5 opacity-60" />
		}
	}

	return <Icon name="file" className="size-5 opacity-60" />
}
